import { Link } from "react-router-dom"
import { useHead, useSeoMeta } from "@unhead/react"
import {Check, Heart, ArrowRight, MapPin, Clock} from 'lucide-react'
import { prestationsFemme, serviceArea, travelFees } from "@/data/site"
import { jsonLdGraphScript, pageCanonical } from "@/lib/seo"

function ServicesFemme() {
  useSeoMeta({
    title: "Massage Femme \u2013 Plouay, Lorient (56)",
    description: "Massages bien-\u00eatre pour la femme \u00e0 Plouay (56240) : \u00ab Douceurs et r\u00e9sonnance \u00bb et \u00ab M\u00e9ridiens et cycles \u00bb pour se recentrer et retrouver son \u00e9quilibre.",
    ogTitle: "Massage Femme \u2013 Plouay, Lorient (56)",
    ogDescription: "Massages bien-\u00eatre pour la femme \u00e0 Plouay (56240) : \u00ab Douceurs et r\u00e9sonnance \u00bb et \u00ab M\u00e9ridiens et cycles \u00bb pour se recentrer et retrouver son \u00e9quilibre.",
    twitterCard: "summary_large_image",
  })

  const femmePageUrl = pageCanonical("/prestations-femme")

  useHead({
    script: [
      jsonLdGraphScript(
        prestationsFemme.map((prestation) => ({
          name: prestation.title,
          description: prestation.summary,
          url: `${femmePageUrl}#${prestation.id}`,
          serviceType: "Massage bien-être femme",
          offers: prestation.pricing,
        })),
        "jsonld-offers-femme",
      ),
    ],
  })

  return (
    <div className="text-[#2d221b]">
      <section className="mx-auto max-w-6xl px-5 pb-8 pt-14 md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <Heart size={14} />
          <span>Prestations Femme</span>
        </div>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-6xl">
          Massages bien-être pour la femme
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e] md:text-lg">
          Des soins corporels pensés pour vous recentrer, libérer les tensions et vous reconnecter à votre
          corps, à chaque étape de votre cycle de vie.
        </p>
      </section>

      <section className="mx-auto max-w-6xl space-y-10 px-5 pb-16 md:px-8">
        {prestationsFemme.map((prestation) => (
          <article
            key={prestation.id}
            id={prestation.id}
            className="overflow-hidden rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs"
          >
            <div className="flex flex-col justify-between p-8 md:p-12">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">{prestation.kicker}</p>
                <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">{prestation.title}</h2>
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#8c6d58]">
                  <Clock size={13} />
                  <span>{prestation.duration}</span>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-[#685548] md:text-sm">
                  {prestation.summary.split("\n\n").map((paragraph, index) => (
                    <span key={index} className="mb-3 block last:mb-0">
                      {paragraph.startsWith("Ce soin s'adresse") ? (
                        <strong className="font-semibold text-[#4a3a30]">{paragraph}</strong>
                      ) : (
                        paragraph
                      )}
                    </span>
                  ))}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {prestation.details.map((detail) => (
                    <li key={detail} className="flex items-start gap-2.5 text-xs leading-relaxed text-[#5e4b3e]">
                      <Check size={15} className="mt-0.5 shrink-0 text-[#b85c38]" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                {"note" in prestation && prestation.note ? (
                  <p className="mt-4 text-xs italic leading-relaxed text-[#8c6d58]">{prestation.note}</p>
                ) : null}
              </div>

              <div className="mt-7">
                <div className="divide-y divide-[#ede0d2] rounded-2xl border border-[#ebdcd0] bg-white p-4.5 shadow-warm-xs md:max-w-sm">
                  {prestation.pricing.map((tarif) => (
                    <div key={tarif.label} className="flex items-center justify-between gap-4 py-2.5 first:pt-0 last:pb-0 text-sm">
                      <span className="text-xs font-medium text-[#4a3a30] leading-snug">{tarif.label}</span>
                      <span className="shrink-0 text-right font-display text-base font-bold text-[#8a4225] tabular-nums whitespace-nowrap">
                        {tarif.price}
                      </span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-warm-sm transition-all hover:scale-[1.02] hover:shadow-warm-md"
                >
                  <span>Prendre contact</span>
                  <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Zone d'intervention */}
      <section className="bg-[#efe1d4] border-y border-[#dfd0c2]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:px-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
            <MapPin size={14} />
            <span>Déplacements</span>
          </div>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Zone d'intervention</h2>
          <p className="mt-3 max-w-2xl text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
            {serviceArea}
          </p>
          <p className="mt-2 max-w-2xl text-xs text-[#8c6d58] leading-relaxed">
            {travelFees}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8">
        <div className="rounded-3xl border border-[#dfd0c2] bg-white p-8 md:p-12 shadow-warm-xs">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Accompagnement personnalisé</span>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Une question, un besoin particulier ?</h2>
          <p className="mt-4 max-w-2xl text-xs leading-relaxed text-[#685548] md:text-sm">
            Chaque femme et chaque besoin sont différents. Si votre situation ne correspond pas exactement
            à l'une de ces prestations, parlons-en : nous adapterons l'accompagnement ensemble.
          </p>
          <p className="mt-3 max-w-2xl text-xs text-[#8c6d58] italic">
            Ces soins sont des accompagnements de bien-être non médicaux et ne remplacent en aucun cas un
            suivi médical.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-[#cbbab0] bg-white/90 px-6 py-3 text-xs font-medium uppercase tracking-[0.14em] text-[#5e4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#2d221b]"
          >
            <span>Me contacter</span>
            <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ServicesFemme
